import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { MapPin, Clock, CheckCircle2, XCircle, Package, ArrowRight, Mail, Phone, ChevronRight, RotateCw } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Sport Fitt Pro';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-80 overflow-hidden bg-ink-950">
          <img src={siteImages.returnsBanner} alt="Returns" className="absolute inset-0 w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent" />
          <div className="absolute bottom-0 right-0 w-2/5 h-2 bg-athletic-500" />
          <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-end pb-12">
            <div>
              <nav className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white">Returns</span>
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Hassle-Free Returns</p>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-wider">
                RETURNS &<br />
                <span className="text-athletic-500">REFUNDS</span>
              </h1>
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-16">

          {/* Return Address - Prominent */}
          <div className="bg-ink-950 text-white p-8 lg:p-10 mb-12 border-l-8 border-athletic-500">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 bg-athletic-500 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-2">Ship Returns To</p>
                <h2 className="font-display text-3xl lg:text-4xl tracking-wider leading-none mb-4">RETURN ADDRESS</h2>
                <p className="text-white text-xl font-bold leading-relaxed">
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Cards */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {[
              { icon: Clock, title: '30-Day Window', desc: 'Return within 30 days of delivery for full refund.' },
              { icon: Package, title: 'Original Condition', desc: 'Items must be unused and in original packaging.' },
              { icon: CheckCircle2, title: 'Full Refund', desc: 'Refund processed within 5-7 business days.' },
            ].map((item, i) => (
              <div key={i} className="bg-white border-2 border-ink-100 hover:border-athletic-500 p-6 text-center transition-all">
                <div className="w-14 h-14 bg-athletic-500 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-xl text-ink-900 tracking-wider mb-2">{item.title.toUpperCase()}</h3>
                <p className="text-ink-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Eligible / Not Eligible */}
          <div className="grid md:grid-cols-2 gap-5 mb-12">
            <div className="bg-white border-2 border-ink-100 p-6">
              <h3 className="font-display text-2xl text-ink-900 tracking-wider mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-athletic-500" /> ELIGIBLE
              </h3>
              <ul className="space-y-3">
                {['Unused items in original packaging','Items returned within 30 days','Defective or damaged products','Wrong items received','Items with all tags attached'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-athletic-500 mt-0.5 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-ink-50 border-2 border-ink-100 p-6">
              <h3 className="font-display text-2xl text-ink-900 tracking-wider mb-4 flex items-center gap-2">
                <XCircle className="w-6 h-6 text-ink-500" /> NOT ELIGIBLE
              </h3>
              <ul className="space-y-3">
                {['Used or worn items','Items without original packaging','Items returned after 30 days','Items damaged by misuse','Personal hygiene items once opened'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-700 text-sm">
                    <XCircle className="w-4 h-4 text-ink-500 mt-0.5 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* How to Return */}
          <div className="bg-white border-2 border-ink-100 p-8 lg:p-10 mb-12">
            <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-2">4 Simple Steps</p>
            <h3 className="font-display text-4xl text-ink-900 tracking-wider mb-8">HOW TO RETURN</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { num: '01', title: 'Contact Us', desc: 'Email or call to request a return authorization.' },
                { num: '02', title: 'Pack Your Item', desc: 'Securely pack in the original packaging.' },
                { num: '03', title: 'Ship It Back', desc: 'Send the package to our return address above.' },
                { num: '04', title: 'Get Refunded', desc: 'Refund processed within 5-7 business days.' },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-5 border-l-4 border-athletic-500 pl-5">
                  <span className="font-display text-4xl text-athletic-500 leading-none">{step.num}</span>
                  <div>
                    <h4 className="font-bold text-ink-900 text-lg mb-1">{step.title}</h4>
                    <p className="text-ink-500 text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Help CTA */}
          <div className="bg-athletic-500 text-white p-8 lg:p-10">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div>
                <h3 className="font-display text-3xl tracking-wider mb-2">NEED HELP WITH A RETURN?</h3>
                <p className="text-white/90">Our customer support team is here to help.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center justify-center gap-2 px-6 py-3 bg-ink-950 hover:bg-white hover:text-ink-950 text-white font-black uppercase tracking-wider text-xs transition-colors">
                  <Mail className="w-4 h-4" /> Email Us
                </a>
                <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-ink-950 hover:bg-ink-950 hover:text-white font-black uppercase tracking-wider text-xs transition-colors">
                  <Phone className="w-4 h-4" /> Call Us
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReturnsPage;
